import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit2, Loader2, X, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  question_bg: "",
  answer_bg: "",
  question_en: "",
  answer_en: "",
  question_es: "",
  answer_es: "",
  order: 0,
  published: true,
  category: "",
};

export default function AdminFAQ({ API, isDark }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/faq`);
      setItems(data.items || []);
    } catch {
      toast.error("Неуспешно зареждане на FAQ");
    }
    setLoading(false);
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      question_bg: item.question_bg || "",
      answer_bg: item.answer_bg || "",
      question_en: item.question_en || "",
      answer_en: item.answer_en || "",
      question_es: item.question_es || "",
      answer_es: item.answer_es || "",
      order: item.order || 0,
      published: item.published !== false,
      category: item.category || "",
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...EMPTY, order: items.length });
  };

  const save = async () => {
    if (!form.question_bg.trim() || !form.answer_bg.trim()) {
      return toast.error("BG въпрос и отговор са задължителни");
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/faq/${editingId}`, form);
        toast.success("Обновен");
      } else {
        await axios.post(`${API}/admin/faq`, form);
        toast.success("Създаден");
      }
      resetForm();
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис");
    }
    setSaving(false);
  };

  const remove = async (id, q) => {
    if (!window.confirm(`Изтриване: "${q.slice(0, 50)}..."?`)) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`);
      toast.success("Изтрит");
      fetch();
    } catch {
      toast.error("Грешка");
    }
  };

  const moveItem = async (idx, direction) => {
    const newItems = [...items];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= newItems.length) return;
    [newItems[idx], newItems[newIdx]] = [newItems[newIdx], newItems[idx]];
    setItems(newItems);
    try {
      await axios.post(`${API}/admin/faq/reorder`, { ordered_ids: newItems.map(i => i.id) });
    } catch {
      fetch();
    }
  };

  const cellCls = isDark ? "text-gray-200" : "text-gray-800";
  const labelCls = `text-[11px] font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`;
  const inputCls = `text-sm ${isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : ""}`;

  return (
    <div className="space-y-5" data-testid="admin-faq">
      {/* Edit form */}
      <Card className={isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-bold ${cellCls}`}>
              {editingId ? "Редактиране" : "Нов FAQ запис"}
            </h3>
            {editingId && (
              <Button variant="ghost" size="sm" onClick={resetForm} data-testid="faq-cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> Отказ
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>BG Въпрос *</label>
              <Input value={form.question_bg}
                onChange={(e) => setForm(f => ({ ...f, question_bg: e.target.value }))}
                className={`h-9 ${inputCls}`} data-testid="faq-qbg" />
            </div>
            <div>
              <label className={labelCls}>EN Question</label>
              <Input value={form.question_en}
                onChange={(e) => setForm(f => ({ ...f, question_en: e.target.value }))}
                className={`h-9 ${inputCls}`} data-testid="faq-qen" />
            </div>
            <div>
              <label className={labelCls}>BG Отговор *</label>
              <Textarea value={form.answer_bg}
                onChange={(e) => setForm(f => ({ ...f, answer_bg: e.target.value }))}
                rows={4} className={inputCls} data-testid="faq-abg" />
            </div>
            <div>
              <label className={labelCls}>EN Answer</label>
              <Textarea value={form.answer_en}
                onChange={(e) => setForm(f => ({ ...f, answer_en: e.target.value }))}
                rows={4} className={inputCls} data-testid="faq-aen" />
            </div>
            <div>
              <label className={labelCls}>ES Pregunta</label>
              <Input value={form.question_es}
                onChange={(e) => setForm(f => ({ ...f, question_es: e.target.value }))}
                className={`h-9 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>ES Respuesta</label>
              <Textarea value={form.answer_es}
                onChange={(e) => setForm(f => ({ ...f, answer_es: e.target.value }))}
                rows={3} className={inputCls} />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input id="faq-published" type="checkbox" checked={form.published}
                onChange={(e) => setForm(f => ({ ...f, published: e.target.checked }))}
                data-testid="faq-published-toggle"
              />
              <label htmlFor="faq-published" className={`text-sm ${cellCls}`}>Публикуван (видим за потребители)</label>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="faq-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {editingId ? "Обнови" : "Добави"}
          </Button>
        </CardContent>
      </Card>

      {/* List */}
      <Card className={isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}>
        <CardContent className="p-4">
          <h3 className={`text-sm font-bold mb-3 ${cellCls}`}>FAQ записи ({items.length})</h3>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : items.length === 0 ? (
            <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Няма записи.</p>
          ) : (
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.id} className={`p-3 rounded-lg border ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)]" : "bg-gray-50 border-gray-100"}`} data-testid={`faq-row-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] font-mono ${isDark ? "text-gray-500" : "text-gray-400"}`}>#{item.order}</span>
                        {item.published ? (
                          <Eye className="w-3 h-3 text-green-500" />
                        ) : (
                          <EyeOff className="w-3 h-3 text-gray-400" />
                        )}
                        <span className={`text-sm font-semibold ${cellCls}`}>{item.question_bg}</span>
                      </div>
                      <p className={`text-xs line-clamp-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{item.answer_bg}</p>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(item)} data-testid={`faq-edit-${idx}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(item.id, item.question_bg)} data-testid={`faq-delete-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
